import { DragEvent, FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Typography from "@mui/material/Typography";

export interface FileInputProps {
  label: string;
  value: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  disabled?: boolean;
  error?: string;
}

export const FileInput: FC<FileInputProps> = ({ label, value, onChange, accept, disabled, error }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const filename = value?.[0]?.name ?? null;

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    const nextTarget = e.relatedTarget;
    if (nextTarget instanceof Node && e.currentTarget.contains(nextTarget)) return;
    setIsDragOver(false);
  };

  const isAccepted = (file: File) => {
    if (!accept) return true;
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    return accept
      .split(",")
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
      .some((token) => {
        if (token.startsWith(".")) return fileName.endsWith(token);
        if (token.endsWith("/*")) return fileType.startsWith(token.slice(0, -1));
        return fileType === token;
      });
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(isAccepted);
    if (droppedFiles.length > 0) {
      onChange(droppedFiles);
    }
  };

  return (
    <FormControl error={!!error} fullWidth disabled={disabled}>
      <InputLabel shrink size="small">
        {label}
      </InputLabel>
      <OutlinedInput
        size="small"
        notched
        label={label}
        inputProps={{ style: { display: "none" } }}
        sx={{
          cursor: disabled ? "default" : "pointer",
          px: 1,
          py: 0.75,
          ...(isDragOver && {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          }),
          "& .MuiOutlinedInput-notchedOutline": isDragOver
            ? { borderColor: "primary.main", borderStyle: "dashed" }
            : undefined,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        startAdornment={
          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              component="span"
              disabled={disabled}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {t("certificate_upload.choose_file")}
            </Button>
            <Typography variant="body2" sx={{ color: filename ? "text.primary" : "text.secondary" }} noWrap>
              {filename ?? `${t("certificate_upload.no_file_chosen")} ${t("certificate_upload.drag_drop_hint")}`}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              hidden
              disabled={disabled}
              onChange={(e) => onChange(e.target.files ? Array.from(e.target.files) : [])}
            />
          </Box>
        }
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export default FileInput;
